import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import companyAccountsService from '../../services/companyAccountsService';
import ExecutiveSummaryCard from './components/ExecutiveSummaryCard';
import ClientOverviewTable from './components/ClientOverviewTable';
import ClientFilters from './components/ClientFilters';
import ClientHealthDashboard from './components/ClientHealthDashboard';
import AlertsPanel from './components/AlertsPanel';

const ClientAdministrationDashboard = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [filters, setFilters] = useState({
    subscriptionTier: 'all',
    paymentStatus: 'all',
    complianceScore: 'all',
    region: 'all',
    searchTerm: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [clients, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [clientsData, summaryData] = await Promise.all([
        companyAccountsService?.getAllCompanyAccounts(),
        companyAccountsService?.getExecutiveSummary()
      ]);

      setClients(clientsData);
      setSummary(summaryData);
    } catch (err) {
      setError(err?.message || 'Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...clients];

    // Search filter
    if (filters?.searchTerm) {
      const term = filters?.searchTerm?.toLowerCase();
      filtered = filtered?.filter(client =>
        client?.companyName?.toLowerCase()?.includes(term) ||
        client?.contactEmail?.toLowerCase()?.includes(term)
      );
    }

    // Subscription tier filter
    if (filters?.subscriptionTier !== 'all') {
      filtered = filtered?.filter(client => client?.subscriptionTier === filters?.subscriptionTier);
    }

    // Payment status filter
    if (filters?.paymentStatus !== 'all') {
      filtered = filtered?.filter(client => {
        if (!client?.nextBillingDate) return filters?.paymentStatus === 'overdue';
        const daysUntilBilling = Math.floor(
          (new Date(client?.nextBillingDate) - new Date()) / (1000 * 60 * 60 * 24)
        );
        if (filters?.paymentStatus === 'current') return daysUntilBilling > 7;
        if (filters?.paymentStatus === 'due_soon') return daysUntilBilling >= 0 && daysUntilBilling <= 7;
        if (filters?.paymentStatus === 'overdue') return daysUntilBilling < 0;
        return true;
      });
    }

    // Compliance score filter
    if (filters?.complianceScore !== 'all') {
      filtered = filtered?.filter(client => {
        const score = client?.complianceScore || 0;
        if (filters?.complianceScore === 'excellent') return score >= 90;
        if (filters?.complianceScore === 'good') return score >= 70 && score < 90;
        if (filters?.complianceScore === 'warning') return score < 70;
        return true;
      });
    }

    setFilteredClients(filtered);
  };

  const handleClientAction = (client, action) => {
    setSelectedClient(client);
    console.log(`Action: ${action} for client:`, client?.companyName);
    // Implement specific actions
  };

  const handleRefresh = () => {
    loadData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <GlobalFilterBar onFilterChange={setGlobalFilter} />
        <ComplianceAlertBanner />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading client data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />

      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Client Administration Dashboard</h1>
            <p className="text-muted-foreground">
              Comprehensive oversight for all PNG HR Analytics client organizations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRefresh}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
            <Button>
              <Icon name="Download" size={16} className="mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-blue-600" />
              <p className="text-blue-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Executive Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <ExecutiveSummaryCard
            title="Total Active Clients"
            value={summary?.activeAccounts || 0}
            subtitle={`${summary?.totalAccounts || 0} total accounts`}
            icon="Users"
            trend="up"
            status="compliant"
          />
          <ExecutiveSummaryCard
            title="Monthly Revenue (Kina)"
            value={`K${(summary?.totalRevenueKina || 0)?.toLocaleString()}`}
            subtitle="Active subscriptions"
            icon="DollarSign"
            trend="up"
            status="compliant"
          />
          <ExecutiveSummaryCard
            title="System Health"
            value={`${summary?.billingHealthPercentage || 0}%`}
            subtitle="Billing health score"
            icon="Activity"
            trend={summary?.billingHealthPercentage >= 80 ? 'up' : 'down'}
            status={summary?.billingHealthPercentage >= 80 ? 'compliant' : 'warning'}
          />
          <ExecutiveSummaryCard
            title="Support Tickets"
            value={summary?.criticalIssues || 0}
            subtitle="Critical issues"
            icon="AlertTriangle"
            trend="down"
            status={summary?.criticalIssues > 5 ? 'critical' : 'compliant'}
          />
        </div>

        {/* Alerts Panel */}
        <AlertsPanel clients={filteredClients} />

        {/* Filters */}
        <ClientFilters
          filters={filters}
          onFilterChange={setFilters}
          totalClients={clients?.length}
          filteredCount={filteredClients?.length}
        />

        {/* Main Client Overview Panel */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-foreground">Client Overview</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Icon name="Filter" size={14} className="mr-1" />
                Advanced Filters
              </Button>
            </div>
          </div>
          <ClientOverviewTable
            clients={filteredClients}
            onClientAction={handleClientAction}
          />
        </div>

        {/* Client Health Dashboard */}
        {selectedClient && (
          <ClientHealthDashboard
            client={selectedClient}
            onClose={() => setSelectedClient(null)}
          />
        )}
      </main>
    </div>
  );
};

export default ClientAdministrationDashboard;